/**
 * DetectStepEnhanced - QBridge & MT5 Detection with Troubleshooting
 *
 * Step 1 of QBridge Setup Wizard (Enhanced Version)
 * - Detects QBridge running status
 * - Detects MT5 terminal and account info
 * - Integrated troubleshooting guide
 * - Visual status indicators
 */

import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    CircularProgress
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ErrorIcon from '@mui/icons-material/Error';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import ComputerIcon from '@mui/icons-material/Computer';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// Components
import TroubleshootingGuide from '../components/TroubleshootingGuide';

const DetectStepEnhanced = ({
    qbridgeRunning,
    qbridgeVersion,
    mt5Detected,
    mt5Account,
    mt5Broker,
    mt5Balance,
    mt5Currency,
    isSearching,
    onNext,
    onBack,
    colors
}) => {
    const [isRetrying, setIsRetrying] = useState(false);
    const [showTroubleshoot, setShowTroubleshoot] = useState(false);

    // Auto-show troubleshoot after 10 seconds if not detected
    useEffect(() => {
        if (!qbridgeRunning) {
            const timer = setTimeout(() => {
                setShowTroubleshoot(true);
            }, 10000);
            return () => clearTimeout(timer);
        }
    }, [qbridgeRunning]);

    const handleRetry = async () => {
        setIsRetrying(true);
        // Force a re-check by waiting briefly
        await new Promise(resolve => setTimeout(resolve, 1000));
        setIsRetrying(false);
    };

    const canProceed = qbridgeRunning && mt5Detected;

    // Status helper
    const getStatusColor = (isOk) => isOk ? colors.primary : colors.textDim;
    const getStatusIcon = (isOk, isLoading) => {
        if (isLoading) return <CircularProgress size={20} sx={{ color: colors.primary }} />;
        if (isOk) return <CheckCircleIcon sx={{ color: colors.primary, fontSize: '1.25rem' }} />;
        return <RadioButtonUncheckedIcon sx={{ color: colors.textDim, fontSize: '1.25rem' }} />;
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
                py: 2
            }}
        >
            {/* ═══════════════════════════════════════════════════════════════════
                QBRIDGE STATUS
                ═══════════════════════════════════════════════════════════════════ */}
            <Box
                sx={{
                    background: qbridgeRunning
                        ? `${colors.primaryBg || 'rgba(39, 181, 109, 0.08)'}`
                        : colors.surface,
                    border: `1px solid ${qbridgeRunning ? colors.primaryBorder : colors.border}`,
                    borderRadius: '12px',
                    p: 2.5,
                    transition: 'all 0.3s ease'
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        {getStatusIcon(qbridgeRunning, !qbridgeRunning && isRetrying)}
                        <Typography
                            sx={{
                                color: colors.text,
                                fontSize: '1rem',
                                fontWeight: 600,
                                fontFamily: "'Inter', sans-serif"
                            }}
                        >
                            QBridge
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            px: 1.5,
                            py: 0.5,
                            borderRadius: '6px',
                            background: qbridgeRunning ? colors.primary : colors.textDim,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5
                        }}
                    >
                        <Box
                            sx={{
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                background: '#FFFFFF',
                                animation: qbridgeRunning ? 'pulse 1.5s ease-in-out infinite' : 'none',
                                '@keyframes pulse': {
                                    '0%, 100%': { opacity: 1 },
                                    '50%': { opacity: 0.5 }
                                }
                            }}
                        />
                        <Typography
                            sx={{
                                color: '#FFFFFF',
                                fontSize: '0.6875rem',
                                fontWeight: 600,
                                fontFamily: "'Inter', sans-serif",
                                textTransform: 'uppercase',
                                letterSpacing: '0.03em'
                            }}
                        >
                            {qbridgeRunning ? 'Connected' : 'Not Detected'}
                        </Typography>
                    </Box>
                </Box>

                {qbridgeRunning ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box
                            sx={{
                                width: 48,
                                height: 48,
                                borderRadius: '12px',
                                background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryLight} 100%)`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: `0 0 20px ${colors.primaryGlow || 'rgba(39, 181, 109, 0.3)'}`
                            }}
                        >
                            <Typography
                                sx={{
                                    color: '#FFFFFF',
                                    fontSize: '1.5rem',
                                    fontWeight: 800,
                                    fontFamily: "'Inter', sans-serif"
                                }}
                            >
                                Q
                            </Typography>
                        </Box>
                        <Box>
                            <Typography
                                sx={{
                                    color: colors.text,
                                    fontSize: '0.9375rem',
                                    fontWeight: 600,
                                    fontFamily: "'Inter', sans-serif"
                                }}
                            >
                                QBridge is running
                            </Typography>
                            <Typography
                                sx={{
                                    color: colors.textMuted,
                                    fontSize: '0.8125rem',
                                    fontFamily: "'Inter', sans-serif"
                                }}
                            >
                                Version {qbridgeVersion || '1.0.0'} • localhost:5556
                            </Typography>
                        </Box>
                    </Box>
                ) : (
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <Box
                                sx={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: '12px',
                                    background: colors.surface,
                                    border: `2px dashed ${colors.border}`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <CircularProgress size={24} sx={{ color: colors.textMuted }} />
                            </Box>
                            <Box>
                                <Typography
                                    sx={{
                                        color: colors.textSecondary,
                                        fontSize: '0.9375rem',
                                        fontWeight: 500,
                                        fontFamily: "'Inter', sans-serif"
                                    }}
                                >
                                    Looking for QBridge...
                                </Typography>
                                <Typography
                                    sx={{
                                        color: colors.textMuted,
                                        fontSize: '0.8125rem',
                                        fontFamily: "'Inter', sans-serif"
                                    }}
                                >
                                    Check your system tray (bottom-right)
                                </Typography>
                            </Box>
                        </Box>

                        {/* System Tray Illustration */}
                        <Box
                            sx={{
                                background: colors.background,
                                borderRadius: '8px',
                                p: 1.5,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 1
                            }}
                        >
                            <Typography sx={{ color: colors.textDim, fontSize: '0.75rem' }}>System Tray:</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <Box sx={{ width: 16, height: 16, borderRadius: '4px', background: colors.textDim, opacity: 0.3 }} />
                                <Box sx={{ width: 16, height: 16, borderRadius: '4px', background: colors.textDim, opacity: 0.3 }} />
                                <Box
                                    sx={{
                                        width: 16,
                                        height: 16,
                                        borderRadius: '4px',
                                        background: colors.primary,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        animation: 'blink 1s ease-in-out infinite',
                                        '@keyframes blink': {
                                            '0%, 100%': { opacity: 1 },
                                            '50%': { opacity: 0.4 }
                                        }
                                    }}
                                >
                                    <Typography sx={{ color: '#FFFFFF', fontSize: '0.5rem', fontWeight: 800 }}>Q</Typography>
                                </Box>
                                <Typography sx={{ color: colors.textMuted, fontSize: '0.6875rem', ml: 0.5 }}>← Look for this</Typography>
                            </Box>
                        </Box>
                    </Box>
                )}
            </Box>

            {/* ═══════════════════════════════════════════════════════════════════
                MT5 STATUS
                ═══════════════════════════════════════════════════════════════════ */}
            <Box
                sx={{
                    background: mt5Detected
                        ? `${colors.primaryBg || 'rgba(39, 181, 109, 0.08)'}`
                        : colors.surface,
                    border: `1px solid ${mt5Detected ? colors.primaryBorder : colors.border}`,
                    borderRadius: '12px',
                    p: 2.5,
                    opacity: qbridgeRunning ? 1 : 0.5,
                    transition: 'all 0.3s ease'
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        {getStatusIcon(mt5Detected, qbridgeRunning && isSearching)}
                        <Typography
                            sx={{
                                color: colors.text,
                                fontSize: '1rem',
                                fontWeight: 600,
                                fontFamily: "'Inter', sans-serif"
                            }}
                        >
                            MetaTrader 5
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            px: 1.5,
                            py: 0.5,
                            borderRadius: '6px',
                            background: mt5Detected ? colors.primary : colors.textDim,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5
                        }}
                    >
                        <Box
                            sx={{
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                background: '#FFFFFF'
                            }}
                        />
                        <Typography
                            sx={{
                                color: '#FFFFFF',
                                fontSize: '0.6875rem',
                                fontWeight: 600,
                                fontFamily: "'Inter', sans-serif",
                                textTransform: 'uppercase',
                                letterSpacing: '0.03em'
                            }}
                        >
                            {mt5Detected ? 'Connected' : 'Not Detected'}
                        </Typography>
                    </Box>
                </Box>

                {mt5Detected ? (
                    <Box
                        sx={{
                            background: colors.background,
                            borderRadius: '10px',
                            p: 2
                        }}
                    >
                        {/* Account Info Grid */}
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                                    <ComputerIcon sx={{ fontSize: '0.875rem', color: colors.textMuted }} />
                                    <Typography
                                        sx={{
                                            color: colors.textMuted,
                                            fontSize: '0.6875rem',
                                            fontFamily: "'Inter', sans-serif",
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em'
                                        }}
                                    >
                                        Account
                                    </Typography>
                                </Box>
                                <Typography
                                    sx={{
                                        color: colors.text,
                                        fontSize: '1.125rem',
                                        fontWeight: 700,
                                        fontFamily: "'Inter', sans-serif"
                                    }}
                                >
                                    {mt5Account || '—'}
                                </Typography>
                            </Box>

                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                                    <ShowChartIcon sx={{ fontSize: '0.875rem', color: colors.textMuted }} />
                                    <Typography
                                        sx={{
                                            color: colors.textMuted,
                                            fontSize: '0.6875rem',
                                            fontFamily: "'Inter', sans-serif",
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em'
                                        }}
                                    >
                                        Broker
                                    </Typography>
                                </Box>
                                <Typography
                                    sx={{
                                        color: colors.text,
                                        fontSize: '0.9375rem',
                                        fontWeight: 600,
                                        fontFamily: "'Inter', sans-serif"
                                    }}
                                >
                                    {mt5Broker || '—'}
                                </Typography>
                            </Box>

                            {mt5Balance !== undefined && mt5Balance !== null && (
                                <Box sx={{ gridColumn: 'span 2' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                                        <AccountBalanceWalletIcon sx={{ fontSize: '0.875rem', color: colors.textMuted }} />
                                        <Typography
                                            sx={{
                                                color: colors.textMuted,
                                                fontSize: '0.6875rem',
                                                fontFamily: "'Inter', sans-serif",
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.05em'
                                            }}
                                        >
                                            Balance
                                        </Typography>
                                    </Box>
                                    <Typography
                                        sx={{
                                            color: colors.primary,
                                            fontSize: '1.25rem',
                                            fontWeight: 700,
                                            fontFamily: "'Inter', sans-serif"
                                        }}
                                    >
                                        {typeof mt5Balance === 'number'
                                            ? `$${mt5Balance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                            : mt5Balance
                                        } {mt5Currency || 'USD'}
                                    </Typography>
                                </Box>
                            )}
                        </Box>

                        {/* Info Note */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 1,
                                mt: 2,
                                pt: 1.5,
                                borderTop: `1px solid ${colors.border}`
                            }}
                        >
                            <InfoOutlinedIcon sx={{ fontSize: '0.875rem', color: colors.info || '#3B82F6', mt: 0.25 }} />
                            <Typography
                                sx={{
                                    color: colors.textMuted,
                                    fontSize: '0.75rem',
                                    fontFamily: "'Inter', sans-serif",
                                    lineHeight: 1.5
                                }}
                            >
                                This is the account that will be synced to Q AI Coach. You can change accounts later in QBridge settings.
                            </Typography>
                        </Box>
                    </Box>
                ) : qbridgeRunning ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box
                            sx={{
                                width: 48,
                                height: 48,
                                borderRadius: '12px',
                                background: colors.surface,
                                border: `2px dashed ${colors.border}`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            {isSearching ? (
                                <CircularProgress size={24} sx={{ color: colors.textMuted }} />
                            ) : (
                                <ShowChartIcon sx={{ color: colors.textDim, fontSize: '1.5rem' }} />
                            )}
                        </Box>
                        <Box>
                            <Typography
                                sx={{
                                    color: colors.textSecondary,
                                    fontSize: '0.9375rem',
                                    fontWeight: 500,
                                    fontFamily: "'Inter', sans-serif"
                                }}
                            >
                                {isSearching ? 'Searching for MT5...' : 'MT5 not detected'}
                            </Typography>
                            <Typography
                                sx={{
                                    color: colors.textMuted,
                                    fontSize: '0.8125rem',
                                    fontFamily: "'Inter', sans-serif"
                                }}
                            >
                                Open MT5 and login to your trading account
                            </Typography>
                        </Box>
                    </Box>
                ) : (
                    <Typography
                        sx={{
                            color: colors.textDim,
                            fontSize: '0.8125rem',
                            fontFamily: "'Inter', sans-serif",
                            textAlign: 'center',
                            py: 2
                        }}
                    >
                        Waiting for QBridge connection...
                    </Typography>
                )}
            </Box>

            {/* ═══════════════════════════════════════════════════════════════════
                TROUBLESHOOTING
                ═══════════════════════════════════════════════════════════════════ */}
            {(!qbridgeRunning || (qbridgeRunning && !mt5Detected)) && (
                <TroubleshootingGuide
                    colors={colors}
                    onRetry={handleRetry}
                    isDetecting={isRetrying || isSearching}
                    variant={!qbridgeRunning ? 'qbridge' : 'mt5'}
                />
            )}

            {/* ═══════════════════════════════════════════════════════════════════
                NAVIGATION BUTTONS
                ═══════════════════════════════════════════════════════════════════ */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mt: 2
                }}
            >
                <Button
                    onClick={onBack}
                    startIcon={<ArrowBackIcon />}
                    sx={{
                        px: 2.5,
                        py: 1,
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        fontFamily: "'Inter', sans-serif",
                        textTransform: 'none',
                        color: colors.textMuted,
                        '&:hover': {
                            backgroundColor: colors.surfaceHover,
                            color: colors.text
                        }
                    }}
                >
                    Back
                </Button>

                <Box sx={{ display: 'flex', gap: 1.5 }}>
                    {!canProceed && (
                        <Button
                            onClick={handleRetry}
                            disabled={isRetrying}
                            startIcon={isRetrying ? <CircularProgress size={16} sx={{ color: colors.textMuted }} /> : <RefreshIcon />}
                            sx={{
                                px: 2.5,
                                py: 1,
                                fontSize: '0.875rem',
                                fontWeight: 500,
                                fontFamily: "'Inter', sans-serif",
                                textTransform: 'none',
                                color: colors.textSecondary,
                                border: `1px solid ${colors.border}`,
                                '&:hover': {
                                    backgroundColor: colors.surfaceHover,
                                    borderColor: colors.borderLight
                                }
                            }}
                        >
                            {isRetrying ? 'Checking...' : 'Retry'}
                        </Button>
                    )}

                    <Button
                        onClick={onNext}
                        disabled={!canProceed}
                        endIcon={<ArrowForwardIcon />}
                        sx={{
                            px: 3,
                            py: 1,
                            fontSize: '0.9375rem',
                            fontWeight: 600,
                            fontFamily: "'Inter', sans-serif",
                            textTransform: 'none',
                            borderRadius: '8px',
                            background: canProceed
                                ? `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryLight} 100%)`
                                : colors.surface,
                            color: canProceed ? '#FFFFFF' : colors.textDim,
                            border: canProceed ? 'none' : `1px solid ${colors.border}`,
                            boxShadow: canProceed ? `0 4px 16px ${colors.primaryGlow || 'rgba(39, 181, 109, 0.3)'}` : 'none',
                            '&:hover': {
                                background: canProceed
                                    ? `linear-gradient(135deg, ${colors.primaryLight} 0%, ${colors.primary} 100%)`
                                    : colors.surface
                            },
                            '&.Mui-disabled': {
                                background: colors.surface,
                                color: colors.textDim,
                                border: `1px solid ${colors.border}`
                            }
                        }}
                    >
                        Continue
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

export default DetectStepEnhanced;
